import apiResponse from '@stores/ApiResponse';
import { useQuery } from '@tanstack/react-query';
import { GroupStore } from '@app/api/GroupStore';
import { ViewCreatorStaleTime } from '@utils/queryCacheConfig';

export type PrizeDrawingWinner = {
  prizeId: number;
  cohortUserId: number;
  name: string;
  /** False when Goody failed to produce a gift link and the coach had to follow up */
  hasGiftLink: boolean;
  drawnAt: string | null;
};

export type PrizeDrawingWeek = {
  /** Program week (1-12) the drawing was for; null for an older prize recorded without one */
  weekNumber: number | null;
  /** Null when no winner was recorded, e.g. nobody met their goal that week */
  winner: PrizeDrawingWinner | null;
};

export type PrizeDrawingParticipant = {
  cohortUserId: number;
  name: string;
  isActive: boolean;
  winCount: number;
  winningWeeks: number[];
};

export type AdminPrizeDrawingsView = {
  cohortId: number;
  cohortName: string;
  weeks: PrizeDrawingWeek[];
  participants: PrizeDrawingParticipant[];
};

export class AdminPrizeDrawingsViewCreator {
  constructor(private readonly store: GroupStore = new GroupStore()) {}

  async createView(groupId: number): Promise<AdminPrizeDrawingsView> {
    const result = await apiResponse.handleApiResponse<AdminPrizeDrawingsView>(
      async () => await this.store.AdminGetPrizeDrawings(groupId)
    );

    return result as AdminPrizeDrawingsView;
  }
}

export const useAdminPrizeDrawingsViewCreator = (groupId: number) =>
  useQuery({
    queryKey: [AdminPrizeDrawingsViewCreator.name, groupId],
    queryFn: () => new AdminPrizeDrawingsViewCreator().createView(groupId),
    staleTime: ViewCreatorStaleTime.AdminPrizeDrawingsViewCreator,
    enabled: groupId > 0,
  });
